import React from 'react';

import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { HTMLAsset, ImageAsset } from '@techstyle/react-assets';

import { mobile, desktop } from '../../styles';

const Container = styled.div`
  display: ${props => (props.showMobile ? `flex` : `none`)};
  flex-direction: column;

  background-color: ${props => props.bgColor || 'transparent'};

  ${desktop`
    flex-direction: row;
    flex-wrap: nowrap;
  `};
`;

const htmlStyles = css`
  ${mobile`
    padding-top: ${props => props.theme.spacing.small}px;
    padding-bottom: ${props => props.theme.spacing.small}px;
  `};

  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-top: ${props => props.theme.spacing.large}px;
  padding-bottom: ${props => props.theme.spacing.medium}px;
`;

// We use a separate CSS variable because this style is being shared
// by the SearchSpringBanner component in SearchLayout
const cellStyles = css`
  ${mobile`
    flex: 0 0 100%;
  `};

  flex-grow: 0;
  flex-shrink: 0;
  flex-basis: 50%;
  height: auto;
  position: relative;

  &:after {
    content: '';
    display: table;
    clear: both;
  }

  ${props => props.html && htmlStyles};
`;

const Cell = styled.div`
  ${cellStyles}
`;

// We use a separate CSS variable because this style is being shared
// by the SearchSpringBanner component in SearchLayout
const styledHTMLAssetStyles = css`
  ${props => props.theme.assets.htmlStyles};
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 ${props => props.theme.spacing.small}px;

  text-align: center;

  h1 {
    line-height: 1.2;
  }

  span {
    display: inline-block;
  }

  p:first-child {
    margin-top: 0;
  }

  .underline,
  .price {
    padding-bottom: 4px;

    border-bottom: 2px solid currentColor;
  }

  @media (min-width: ${props => props.theme.breakpoints.xlarge}px) {
    padding: 0 30px;
    h1 {
      font-size: ${48 / 16}em;
    }
    p,
    span {
      font-size: ${20 / 16}em;
    }
  }
`;

const StyledHTMLAsset = styled(HTMLAsset)`
  ${styledHTMLAssetStyles}
`;

const Dot = styled.div`
  /* Hide at a wider viewport so it doesn’t overlap the title */
  @media (max-width: ${props => props.theme.breakpoints.xlarge - 1}px) {
    display: none;
  }
  position: absolute;
  top: 20px;
  right: 20px;

  align-items: center;
  display: flex;
  justify-content: center;
  height: 76px;
  width: 76px;

  background-color: ${props => props.theme.colors.default};
  border-radius: 100%;
  color: ${props => props.theme.colors.white};
  text-align: center;
`;

export default class ProductHeaderFancy extends React.PureComponent {
  static propTypes = {
    asset: PropTypes.object.isRequired,
    className: PropTypes.string,
  };

  static defaultProps = {};

  render() {
    const { asset, className } = this.props;
    const { options } = asset;
    const hasDot = options.customVars.dotText;
    const hasSecondaryImage = options.hoverImage;
    const altText = options.customVars.altText
      ? options.customVars.altText
      : '';

    return (
      <Container
        className={className}
        bgColor={options.bgColor}
        showMobile={options.customVars.showOnMobile}
      >
        <ImageAsset asset={asset} key={asset.imageId} alt={altText} />
        <Cell html>
          <StyledHTMLAsset asset={asset} />
          {hasSecondaryImage && (
            <ImageAsset
              hoverImage
              alt={altText}
              asset={asset}
              key={asset.imageId}
            />
          )}
          {hasDot && <Dot>{options.customVars.dotText}</Dot>}
        </Cell>
      </Container>
    );
  }
}

ProductHeaderFancy.htmlStyles = htmlStyles;
ProductHeaderFancy.cellStyles = cellStyles;
ProductHeaderFancy.styledHTMLAssetStyles = styledHTMLAssetStyles;
